import { DefaultColumns } from "@/models/default.model";
import {SearchResult} from "@/models/search.model";
import { sportmomentApi, sportmomentSearchApiEndpoint } from "~/utils/axios.util";
import {getTimeInSeconds} from "@/helpers/time.helper";

export interface Activiteit {
    SportmomentId: number
    Naam: string
    Sportaanbieder: string
    BeginDatum: Date
    EindDatum: Date
    InschrijvenVanafDatum?: Date
    InschrijvenTotDatum?: Date
    MinLeeftijd: number
    MaxLeeftijd: number
    ExtraKosten: boolean
    ExtraInformatie: string
    AantalPlaatsen: number
    TotaalAantalPlaatsen: number
    AantalInschrijvingen: number
    TotaalAantalInschrijvingen: number
    Aanbod?: number
    Kosten?: number
    DateModified: string
    AfbeeldingUrl: string
    SportaanbiederItem: Aanbieder
    PlatformItem: PlatformItem
    LocatieItem: LocatieItem
    ActiviteittypenTranslated: string[]
    SportenTranslated: string[]
    CultuuractiviteitenTranslated: string[]
    SpecifiekAanbod: ActiviteitKenmerk[]
    FysiekeInspanning: ActiviteitKenmerk[]
    Doelgroep: ActiviteitKenmerk[]
    Leeftijden: ActiviteitKenmerk[]
    Voordeel: ActiviteitKenmerk[]
    Tag: ActiviteitKenmerk[]
    Inclusief: ActiviteitKenmerk[]
    AanbodInclusief: ActiviteitKenmerk[]
    Weekdag: number
    Platform: number
    SportKenmerken: any
    Lessen: ActiviteitLesWithTrainers[]
    Type: string
    BetalingsType: string
    Beschrijving: string
    ExtraKostenBeschrijving: string
    Sportmomenttype: number
}

export interface Aanbieder {
    Id: string
    Naam: string
    Logo: string
    ReviewScore: number
    ReviewCount: number
    Activiteiten: number
    ExtraKosten: boolean
    Locaties: LocatieItem[]
    ActiviteittypenTranslated: string[]
    SportenTranslated: string[]
    CultuuractiviteitenTranslated: string[]
}

export interface PlatformItem {
    Id: number
    Naam: string
    Link: string
}

export interface LocatieItem {
    Id: string
    Naam: string
    Adres: string
    Woonplaats: string
    Postcode: any
    Longitude: number
    Latitude: number
    Afstand: number
}

export interface ActiviteitItem {
    Aanbod: any
    AantalInschrijvingen: number
    AantalPlaatsen: number
    Activiteittype: number
    BeginDatum: Date
    Bekostigingstype: number
    Beschrijving: string
    DateCreated: string
    DateModified: string
    Deleted: boolean
    EindDatum: Date
    ExtraInformatie: string
    ExtraKosten: boolean
    ExtraKostenBeschrijving: string
    HoofdplatformLink: string
    InhalenMogelijk: boolean
    InhalenMogelijkEmailadres: any
    InschrijvenVanafDatum: Date | null
    InschrijvenTotDatum: Date | null
    IsGolfstart2: boolean
    Kosten: number
    Locatie: string
    MaxLeeftijd: number
    MinLeeftijd: number
    MinimumAantalDeelnemers: any
    Naam: string
    NotificatieTijdMinimumAantalDeelnemers: number
    ReeksSportmoment: number
    ServiceKosten: any
    Sportaanbieder: string
    SportmomentId: number
    Sportmomenttype: number
    Status: number
    TotaalAantalInschrijvingen: number
    TotaalAantalPlaatsen: number
    Type: number
}

export enum BekostigingsType {
    CREDITS = 1,
    GRATIS = 2,
    BETALING = 3,
    OPLOCATIE = 4 
}

export interface Les {
    Naam: string
    BeginDatum: Date
    EindDatum: Date
}

export interface IActiviteitSearchParams {
    type: string;
    weekdag_facet?: number[];
    fysiekeinspanning_facet?: string[];
    voordeel_facet?: string[];
    tag_facet?: string[];
    aanbod_facet?: string[];
    doelgroep_facet?: string[];
    specifiekaanbod_facet?: string[];
    sporttypen_facet?: string[];
    activiteittypen_facet?: string[];
    extrakosten_facet?: string[];
    betalingstype_facet?: string[];
    golfstarttype_facet?: string[];
    sportkenmerken_facet?: string[];
    plaatsen_facet?: string[];
    wijken_facet?: string[];
    wijkengemeenten_facet?: string[];
    sporten_facet?: string[];
    cultuuractiviteiten_facet?: string[];
    soortactiviteit_facet?: string[];
    activiteiten_facet?: string[];
    platform_facet?: number;
    activatie_facet?: number;
    promootplaatsen_facet?: string;
    fromPostcode_facet?: number;
    toPostcode_facet?: number;
    facetlist?: string[]
    query?: string;
    sportaanbieder?: string;
    fromStartDatum?: Date;
    toStartDatum?: Date;
    centerLon?: number;
    centerLat?: number;
    distanceInKms?: number;
    fromStartTijdSeconds?: number;
    toStartTijdSeconds?: number;
    minLeeftijd?: number;
    maxLeeftijd?: number
    Id?: string;
    format?: string;
    fromReviewScore?: string;
    toReviewScore?: number
    gs2_index?: string;
    pageSize: number
    offset?: number;
    OrderBy: string;
    Desc?: boolean;
    groupBy?: string;
}

export interface ActiviteitKenmerk {
    SportmomentKenmerkId: number;
    Sportmoment: number;
    Kenmerk: number;
    Categorie: number;
}

export interface ActiviteitLesWithTrainers extends ActiviteitLes {
    Trainers: ActiviteitLesTrainer[];
}

export interface ActiviteitLes extends DefaultColumns {
    SportmomentLesId: number;
    Sportmoment: number;
    Naam: string;
    Beschrijving: string;
    BeginDatum: Date;
    EindDatum: Date;
    Locatie: string | null;
    Lesnummer: number;
}

export interface ActiviteitLesTrainer extends DefaultColumns {
    SportmomentLesTrainerId: number;
    SportmomentLes: number;
    Trainer: number;
}

export interface MarkerData {
    SportmomentId: number,
    SportaanbiederId: string,
    Longitude: number,
    Latitude: number,
}

class ActiviteitRepository {
    async search(params: IActiviteitSearchParams) {
        const response = await sportmomentSearchApiEndpoint.get<SearchResult<Activiteit, Aanbieder>>('/search', {
            params: {
                ...params,
                facetlist: params.facetlist && params.facetlist.length > 0 ? params.facetlist.join(",") : undefined,
                weekdag_facet: params.weekdag_facet && params.weekdag_facet.length > 0 ? params.weekdag_facet.join("|") : undefined,
                fysiekeinspanning_facet: params.fysiekeinspanning_facet && params.fysiekeinspanning_facet.length > 0 ? params.fysiekeinspanning_facet.join("|") : undefined,
                voordeel_facet: params.voordeel_facet && params.voordeel_facet.length > 0 ? params.voordeel_facet.join("|") : undefined,
                tag_facet: params.tag_facet && params.tag_facet.length > 0 ? params.tag_facet.join("|") : undefined,
                aanbod_facet: params.aanbod_facet && params.aanbod_facet.length > 0 ? params.aanbod_facet.join("|") : undefined,
                doelgroep_facet: params.doelgroep_facet && params.doelgroep_facet.length > 0 ? params.doelgroep_facet.join("|") : undefined,
                specifiekaanbod_facet: params.specifiekaanbod_facet && params.specifiekaanbod_facet.length > 0 ? params.specifiekaanbod_facet.join("|") : undefined,
                sporttypen_facet: params.sporttypen_facet && params.sporttypen_facet.length > 0 ? params.sporttypen_facet.join("|") : undefined,
                activiteittypen_facet: params.activiteittypen_facet && params.activiteittypen_facet.length > 0 ? params.activiteittypen_facet.join("|") : undefined,
                extrakosten_facet: params.extrakosten_facet && params.extrakosten_facet.length > 0 ? params.extrakosten_facet.join("|") : undefined,
                soortactiviteit_facet: params.soortactiviteit_facet && params.soortactiviteit_facet.length > 0 ? params.soortactiviteit_facet.join("|") : undefined,
                golfstarttype_facet: params.golfstarttype_facet && params.golfstarttype_facet.length > 0 ? params.golfstarttype_facet.join("|") : undefined,
                sportkenmerken_facet: params.sportkenmerken_facet && params.sportkenmerken_facet.length > 0 ? params.sportkenmerken_facet.join("|") : undefined,
                plaatsen_facet: params.plaatsen_facet && params.plaatsen_facet.length > 0 ? params.plaatsen_facet.join("|") : undefined,
                sporten_facet: params.sporten_facet && params.sporten_facet.length > 0 ? params.sporten_facet.join("|") : undefined,
                wijken_facet: params.wijken_facet && params.wijken_facet.length > 0 ? params.wijken_facet.join("|") : undefined,
                wijkengemeenten_facet: params.wijkengemeenten_facet && params.wijkengemeenten_facet.length > 0 ? params.wijkengemeenten_facet.join("|") : undefined,
                betalingstype_facet: params.betalingstype_facet && params.betalingstype_facet.length > 0 ? params.betalingstype_facet.join("|") : undefined,
                cultuuractiviteiten_facet: params.cultuuractiviteiten_facet && params.cultuuractiviteiten_facet.length > 0 ? params.cultuuractiviteiten_facet.join("|") : undefined,
                fromStartDatum: getTimeInSeconds(params.fromStartDatum),
                toStartDatum: getTimeInSeconds(params.toStartDatum, true)
            }
        })
        return response.data
    }

    async getById(id: number, platformId: number) {
        const response = await sportmomentApi.get<ActiviteitItem>('/api/sportmoment', {
            params: {
                id: id,
                Platform: platformId
            }
        })

        return response.data;
    };

    async getSportactiviteitKenmerken(id: number) {
        const response = await sportmomentApi.get<ActiviteitKenmerk[]>('/api/sportmomentkenmerk', {
            params: {
                sportmoment: id
            }
        })

        return response.data
    }

    async getSportactiviteitLessen(id: number) {
        const response = await sportmomentApi.get<ActiviteitLesWithTrainers[]>('/api/sportmomentles', {
            params: {
                sportmoment: id
            }
        })

        return response.data
    }
    
    async getMarkers(params: IActiviteitSearchParams, groupSportaanbieder: boolean) {
        const response = await sportmomentSearchApiEndpoint.get<SearchResult<MarkerData, MarkerData>>('/search', {
            params: {
                ...params,
                facetlist: params.facetlist?.join(","),
                weekdag_facet: params.weekdag_facet?.join("|"),
                fysiekeinspanning_facet: params.fysiekeinspanning_facet?.join("|"),
                voordeel_facet: params.voordeel_facet?.join("|"),
                tag_facet: params.tag_facet?.join("|"),
                aanbod_facet: params.aanbod_facet?.join("|"),
                doelgroep_facet: params.doelgroep_facet?.join("|"),
                specifiekaanbod_facet: params.specifiekaanbod_facet?.join("|"),
                sporttypen_facet: params.sporttypen_facet?.join("|"),
                activiteittypen_facet: params.activiteittypen_facet?.join("|"),
                extrakosten_facet: params.extrakosten_facet?.join("|"),
                golfstarttype_facet: params.golfstarttype_facet?.join("|"),
                sportkenmerken_facet: params.sportkenmerken_facet?.join("|"),
                plaatsen_facet: params.plaatsen_facet?.join("|"),
                sporten_facet: params.sporten_facet?.join("|"),
                cultuuractiviteiten_facet: params.cultuuractiviteiten_facet?.join("|"),
                fromPostcode_facet: params.fromPostcode_facet,
                toPostcode_facet: params.toPostcode_facet,
                fromStartDatum: getTimeInSeconds(params.fromStartDatum),
                toStartDatum: getTimeInSeconds(params.toStartDatum),
                pageSize: 999999,
                distanceInKms: undefined,
                centerLon: undefined,
                centerLat: undefined,
                offset: 0,
                format: "markers",
                groupBy: groupSportaanbieder ? "Sportaanbieder" : undefined,
                OrderBy: groupSportaanbieder ? "StartDatum" : undefined
            }
        });
        
        return response.data;
    }
}

export default new ActiviteitRepository();
